import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getRelativePosition } from "../../../../helpers";
import {
  ITEM_TYPES,
  ItemType,
  LINE_ORIENTATIONS,
  Mask,
  NullableHeightSize,
  Position,
  SHAPE_TYPES,
  Size,
  Stroke,
} from "../../../../types";
import { DEFAULT_MASK, isMasked } from "../../../../helpers/maskHelper";
import { MaskEditor } from "./MaskEditor";
import { EditOutlined } from "@ant-design/icons";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useMoveItemWithArrows } from "../../../../hooks/useMoveItemsWithArrows";
import { useDeleteItem } from "../../../../hooks/useDeleteItem";
import { mapRelativeToPixels } from "../../../../helpers/itemsHelper";
import { BORDER_COLOR } from "../../../../constants";
import { MoveableEntry, useMoveableRegistry } from "../moveableRegistry";

const SNAP_TO_FULL_THRESHOLD = 0.98;
const snapToFull = (v: number) => (v > SNAP_TO_FULL_THRESHOLD ? 1 : v);

const normalizeAngle = (deg: number) => Math.round(((deg % 360) + 360) % 360);

export const DraggableComponent = <
  SizeType extends Size | NullableHeightSize = Size | NullableHeightSize,
>({
  children,
  position,
  size,
  layerIndex,
  stroke,
  drag,
  resize,
  item,
  saveRatio = false,
  rotation = 0,
  previewWidth,
  previewHeight,
  onDragStop,
  onFocus,
  onRotate,
  onEditStart,
  bounds,
  mask,
  onMask,
}: {
  children: ReactNode;
  position: Position;
  size: SizeType;
  layerIndex: number;
  stroke?: Stroke;
  drag: boolean;
  resize: boolean;
  item: ItemType;
  saveRatio?: boolean;
  rotation?: number;
  previewWidth: number;
  previewHeight: number;
  onDragStop: (params: {
    id?: string;
    position?: Position;
    stroke?: Stroke;
    size?: SizeType;
    mask?: Mask;
  }) => void;
  onFocus?: () => void;
  onRotate?: (deg: number) => void;
  onEditStart?: () => void;
  /** Optional element to constrain dragging within (used by the logo). */
  bounds?: string | Element;
  /** Crop mask (visible sub-rectangle of the item box), for media items. */
  mask?: Mask;
  /** Commit a new mask (only used while editing a selected media item). */
  onMask?: (mask: Mask) => void;
}) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const registry = useMoveableRegistry();

  const {
    currentItem,
    selectedItemIds,
    isInteracting,
    setCurrentItem,
    toggleSelection,
  } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
    selectedItemIds: store.selectedItemIds,
    isInteracting: store.isInteracting,
    setCurrentItem: store.setCurrentItem,
    toggleSelection: store.toggleSelection,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const selectionKey = item.id ?? item.type;

  const isTitleItem = item.type === ITEM_TYPES.Title;
  const isTextItem = item.type === ITEM_TYPES.Subtitles || isTitleItem;
  const isCustomTextItem = item.type === ITEM_TYPES.CustomText;
  const isShapeItem = item.type === ITEM_TYPES.Shape;
  const isCircleShape = isShapeItem && item.shapeType === SHAPE_TYPES.CIRCLE;
  const isNotLineShape = isShapeItem && !item.orientation;
  const isHorizontalLine =
    isShapeItem && item.orientation === LINE_ORIENTATIONS.HORIZONTAL;
  const isVerticalLine =
    isShapeItem && item.orientation === LINE_ORIENTATIONS.VERTICAL;
  const isMultimediaItem =
    item.type === ITEM_TYPES.Logo ||
    item.type === ITEM_TYPES.Presenter ||
    item.type === ITEM_TYPES.Image ||
    item.type === ITEM_TYPES.Video;
  const isTableItem = item.type === ITEM_TYPES.Table;

  const isSelected = selectedItemIds.includes(selectionKey);
  const isGroupSelection = selectedItemIds.length > 1;
  const isSingleSelected = isSelected && selectedItemIds.length === 1;

  const [isHovered, setIsHovered] = useState(false);

  useMoveItemWithArrows(
    item.id,
    isSingleSelected,
    position,
    size,
    stroke?.width ?? 0,
    previewWidth,
    previewHeight,
    onDragStop
  );

  useDeleteItem(currentItem);

  const outlineStyle = useMemo(() => {
    // Single selection is outlined by the Moveable controller itself; only
    // highlight group members and hovered (non-selected) items here.
    if (isSelected && isGroupSelection)
      return {
        outline: `2px solid color-mix(in srgb, ${BORDER_COLOR} 70%, transparent)`,
        outlineOffset: "-2px",
      };
    // Hovered (not selected): for a cropped item, the dotted outline is drawn
    // on the cropped area (see hover overlay below), not the whole media box.
    if (!isSelected && isHovered && !isInteracting && !isMasked(mask))
      return {
        outline: `2px dotted color-mix(in srgb, ${BORDER_COLOR} 70%, transparent)`,
        outlineOffset: "-2px",
      };
    return { outline: "unset" };
  }, [isSelected, isGroupSelection, isHovered, isInteracting, mask]);

  // ─── Geometry (relative 0–1 ↔ pixels, with stroke offset) ───
  const strokeWidth = Math.round((stroke?.width ?? 0) * previewWidth);
  const doubleStrokeWidth = strokeWidth * 2;
  const isTextItemWithoutHeight =
    (isTextItem || isCustomTextItem) && size.height == null;

  const leftPx = mapRelativeToPixels(position.x, previewWidth) - strokeWidth;
  const topPx = mapRelativeToPixels(position.y, previewHeight) - strokeWidth;
  const widthPx =
    mapRelativeToPixels(size.width, previewWidth) + doubleStrokeWidth;
  const heightPx = size.height
    ? mapRelativeToPixels(size.height, previewHeight) + doubleStrokeWidth
    : undefined;

  // ─── Resize handle directions (mirror the previous per-side enable logic) ───
  const resizeDirections = useMemo(() => {
    const isTopBottom =
      (isShapeItem && !isHorizontalLine && !isCircleShape) ||
      ((isTextItem || isCustomTextItem) && size.height != null) ||
      isTableItem ||
      (isMultimediaItem && !saveRatio);
    const isRightLeft =
      (isShapeItem && !isVerticalLine && !isCircleShape) ||
      isCustomTextItem ||
      isTextItem ||
      isTableItem ||
      (isMultimediaItem && !saveRatio);
    const isCorners = isMultimediaItem || isNotLineShape || isCircleShape;
    const dirs: string[] = [];
    if (isTopBottom) dirs.push("n", "s");
    if (isRightLeft) dirs.push("e", "w");
    if (isCorners) dirs.push("nw", "ne", "sw", "se");
    return dirs;
  }, [
    isShapeItem,
    isHorizontalLine,
    isVerticalLine,
    isCircleShape,
    isNotLineShape,
    isTextItem,
    isCustomTextItem,
    isTableItem,
    isMultimediaItem,
    saveRatio,
    size.height,
  ]);

  // ─── Register with the centralized Moveable controller ───
  // Keep the entry (config + commit closures) fresh on every render so the
  // layer always reads current geometry; updating the ref in an effect (not
  // during render) keeps React's rules-of-refs happy.
  const entryDataRef = useRef<MoveableEntry>();
  useEffect(() => {
    entryDataRef.current = {
      getElement: () => targetRef.current,
      getFrame: () => ({
        translate: [leftPx, topPx],
        rotate: rotation,
        width: targetRef.current?.offsetWidth ?? widthPx,
        height: targetRef.current?.offsetHeight ?? heightPx ?? 0,
      }),
      getConfig: () => ({
        draggable: drag,
        resizable: resize,
        rotatable: !!onRotate,
        keepRatio: saveRatio,
        resizeDirections,
        autoHeight: isTextItemWithoutHeight,
        canDelete: false,
        canDuplicate: false,
        bounds,
        mask,
      }),
      commitDrag: (left, top) => {
        const updatedPosition = {
          x: getRelativePosition(left + strokeWidth, previewWidth),
          y: getRelativePosition(top + strokeWidth, previewHeight),
        };
        onDragStop(
          item.id
            ? { id: item.id, position: updatedPosition }
            : { position: updatedPosition }
        );
        addTemplateUpdate();
      },
      commitResize: ({ leftPx: left, topPx: top, widthPx: w, heightPx: h }) => {
        const widthRel = snapToFull((w - doubleStrokeWidth) / previewWidth);
        const heightRel = isTextItemWithoutHeight
          ? null
          : snapToFull((h - doubleStrokeWidth) / previewHeight);
        const updatedSize = { width: widthRel, height: heightRel } as SizeType;
        const updatedPosition = {
          x:
            widthRel === 1
              ? 0
              : getRelativePosition(left + strokeWidth, previewWidth),
          y:
            heightRel === 1
              ? 0
              : getRelativePosition(top + strokeWidth, previewHeight),
        };
        onDragStop(
          item.id
            ? { id: item.id, size: updatedSize, position: updatedPosition }
            : { size: updatedSize, position: updatedPosition }
        );
        addTemplateUpdate();
      },
      commitRotate: (deg) => {
        onRotate?.(normalizeAngle(deg));
        addTemplateUpdate();
      },
    };
  });

  // Depend on the stable register/unregister fns (not the whole registry
  // value, which changes identity on every version bump) so this effect runs
  // once per mount — otherwise unregister+register would re-bump version and
  // spin into an infinite loop.
  const register = registry?.register;
  const unregister = registry?.unregister;
  useEffect(() => {
    if (!register || !unregister) return;
    const stable: MoveableEntry = {
      getElement: () => entryDataRef.current?.getElement() ?? null,
      getFrame: () =>
        entryDataRef.current?.getFrame() ?? {
          translate: [0, 0],
          rotate: 0,
          width: 0,
          height: 0,
        },
      getConfig: () =>
        entryDataRef.current?.getConfig() ?? {
          draggable: false,
          resizable: false,
          rotatable: false,
          keepRatio: false,
          resizeDirections: [],
          autoHeight: false,
          canDelete: false,
          canDuplicate: false,
        },
      commitDrag: (l, top) => entryDataRef.current?.commitDrag(l, top),
      commitResize: (rect) => entryDataRef.current?.commitResize(rect),
      commitRotate: (d) => entryDataRef.current?.commitRotate(d),
    };
    register(selectionKey, stable);
    return () => unregister(selectionKey);
  }, [register, unregister, selectionKey]);

  // ─── Selection ───
  const handleSelect = (e: ReactMouseEvent) => {
    const isMultiSelect = e.metaKey || e.ctrlKey;
    if (isMultiSelect) {
      const wasSelected = selectedItemIds.includes(selectionKey);
      const next = wasSelected
        ? selectedItemIds.filter((k) => k !== selectionKey)
        : [...selectedItemIds, selectionKey];
      toggleSelection(selectionKey);
      // Restore single-item settings only when this item becomes the sole one
      if (next.length === 1 && next[0] === selectionKey) setCurrentItem(item);
      return;
    }
    if (isSingleSelected) return;
    setCurrentItem(item);
    onFocus?.();
  };

  // ─── Crop mask (media items) ───
  // While the item is selected the mask is editable (full media shown, the
  // outside dimmed by MaskEditor). The crop clip itself lives on the media
  // element (see TemplateImages/Videos) so it travels with enter/leave
  // animations; the box only clips to itself while editing, to contain the
  // dimming wash.
  const isMaskEditing = !!onMask && isSingleSelected;
  // For a cropped item that isn't being edited, only the crop region should be
  // clickable/hoverable — not the transparent cropped-out parts of the box. The
  // box is made non-interactive and a crop-shaped hit area carries the handlers.
  const useCropHitArea = !!onMask && isMasked(mask) && !isSingleSelected;

  // Commit a mask edit. When a crop drag pushed past the media edge, `itemDelta`
  // carries the item move, so position + mask commit together.
  const handleMaskCommit = (
    newMask: Mask,
    itemDelta: { x: number; y: number } | null
  ) => {
    if (itemDelta && (itemDelta.x !== 0 || itemDelta.y !== 0)) {
      onDragStop({
        ...(item.id ? { id: item.id } : {}),
        position: {
          x: getRelativePosition(
            leftPx + itemDelta.x + strokeWidth,
            previewWidth
          ),
          y: getRelativePosition(
            topPx + itemDelta.y + strokeWidth,
            previewHeight
          ),
        },
        mask: newMask,
      });
    } else {
      onMask?.(newMask);
    }
    addTemplateUpdate();
  };

  return (
    <div
      ref={targetRef}
      data-moveable-key={selectionKey}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: widthPx,
        height: isTextItemWithoutHeight ? "auto" : heightPx,
        transform: `translate(${leftPx}px, ${topPx}px) rotate(${rotation}deg)`,
        transformOrigin: "center",
        boxSizing: "border-box",
        // Only clip to the box while editing (to contain the dimming wash);
        // otherwise leave it open so an animated crop can move past the edges.
        overflow: isMaskEditing ? "hidden" : undefined,
        zIndex: layerIndex,
        cursor: isSelected ? "move" : "pointer",
        // Cropped & not editing: the box ignores pointer events so only the
        // crop-shaped hit area below responds to clicks/hover.
        pointerEvents: useCropHitArea ? "none" : undefined,
        ...outlineStyle,
      }}
      onMouseDown={handleSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {useCropHitArea && (
        <div
          data-interactive
          onMouseDown={handleSelect}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            position: "absolute",
            left: `${mask.x * 100}%`,
            top: `${mask.y * 100}%`,
            width: `${mask.width * 100}%`,
            height: `${mask.height * 100}%`,
            // Ellipse masks only respond within the ellipse (clip-path also
            // clips hit-testing).
            clipPath: mask.shape === "ellipse" ? "ellipse(50% 50%)" : undefined,
            pointerEvents: "auto",
            cursor: "pointer",
          }}
        />
      )}
      {isMaskEditing && (
        <MaskEditor
          mask={mask ?? DEFAULT_MASK}
          boxWidth={widthPx}
          boxHeight={heightPx ?? 0}
          boxLeft={leftPx}
          boxTop={topPx}
          canvasWidth={previewWidth}
          canvasHeight={previewHeight}
          boxRef={targetRef}
          requestMoveableUpdate={registry?.requestUpdateRect}
          rotation={rotation}
          onCommit={handleMaskCommit}
        />
      )}
      {/* Hover outline traces the cropped area, not the whole media box. */}
      {isHovered && !isSelected && !isInteracting && isMasked(mask) && (
        <div
          style={{
            position: "absolute",
            left: `${mask.x * 100}%`,
            top: `${mask.y * 100}%`,
            width: `${mask.width * 100}%`,
            height: `${mask.height * 100}%`,
            borderRadius: mask.shape === "ellipse" ? "50%" : undefined,
            outline:
              mask.shape === "ellipse"
                ? undefined
                : `2px dotted color-mix(in srgb, ${BORDER_COLOR} 70%, transparent)`,
            border:
              mask.shape === "ellipse"
                ? `2px dotted color-mix(in srgb, ${BORDER_COLOR} 70%, transparent)`
                : undefined,
            outlineOffset: "-2px",
            pointerEvents: "none",
            boxSizing: "border-box",
          }}
        />
      )}
      {(isCustomTextItem || isTitleItem) && isHovered && !isInteracting && (
        <EditOutlined
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            color: BORDER_COLOR,
            cursor: "pointer",
          }}
          onClick={() => {
            onEditStart?.();
          }}
        />
      )}
    </div>
  );
};
